import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from '@aimsos/i18n';

import { NON_FIELD_SUPPORT, supportFor } from '../utils/nonFieldBindings.js';
import BindingSlotRow from './BindingSlotRow.jsx';
import NonFieldBindingRow, { AutoBindingRow } from './NonFieldBindingRow.jsx';
import TimeBindingRow from './TimeBindingRow.jsx';
import InlineNote from './InlineNote.jsx';

/**
 * `WHAT THE WIDGET SHOWS` — every key the type's `bindings` declares
 * (CT-5183 · fix/widgets).
 *
 * Its own section, beside the type's `display` options and separate from
 * them: `bindings` says **which data** goes where and `display` says how it
 * looks, and the catalog publishes both per type.
 *
 * Three kinds of row, and no key of the contract is left out of the form:
 *
 * 1. **A field slot** — answered with one of the Data step's measures,
 *    groupers or exposed columns, required and optional alike.
 * 2. **The record key** — read-only: a slot that accepts `UUID` alone has one
 *    candidate, and the backend points it at the record uuid it adds itself.
 * 3. **A non-field binding** — `time` has its own editor, a plain enum gets a
 *    select, and the three with a shape of their own (`comparisons`,
 *    `relationships`, `context_queries`) are drawn with a red mark saying the
 *    builder cannot author them yet. Showing them is the point: the catalog
 *    declares the field, and a gap nobody can see is a gap nobody reports.
 *
 * Nothing is filled on the author's behalf any more (CT-5725): the Data step
 * publishes a plain record set, so a slot left blank is a slot the contract
 * will not carry — which is what the warning above the type grid says.
 */
function BindingSlotsSection({
  slots,
  nonFieldSlots,
  assignments,
  nonFieldValues,
  columns,
  timeCandidates,
  isLoading,
  onChange,
  onNonFieldChange,
}) {
  const { t } = useTranslation('dashboards');

  const fieldSlots = useMemo(() => slots.filter((slot) => !slot.isRecordKey), [slots]);
  const recordKeySlots = useMemo(() => slots.filter((slot) => slot.isRecordKey), [slots]);

  const hasColumns = columns.length > 0;

  if (!fieldSlots.length && !nonFieldSlots.length && !recordKeySlots.length) return null;

  return (
    <section className="flex flex-col gap-3">
      <header className="flex flex-col">
        <h3 className="m-0 text-[11px] font-semibold uppercase tracking-wide text-content-secondary">
          {t('widgetBuilder.bindingSlots.title')}
        </h3>
        <p className="m-0 text-xs text-content-muted">
          {t('widgetBuilder.bindingSlots.subtitle')}
        </p>
      </header>

      {/* While the columns load the rows render their own loading state; the
          note is for a Data step that really has nothing in it yet. */}
      {!hasColumns && !isLoading ? (
        <InlineNote>{t('widgetBuilder.bindingSlots.noSources')}</InlineNote>
      ) : null}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {hasColumns || isLoading ? fieldSlots.map((slot) => (
          <BindingSlotRow
            key={slot.key}
            slot={slot}
            value={assignments[slot.key]}
            columns={columns}
            isLoading={isLoading}
            onChange={onChange}
          />
        )) : null}

        {recordKeySlots.map((slot) => (
          <AutoBindingRow key={slot.key} slotKey={slot.key} required={slot.required} />
        ))}

        {nonFieldSlots.map((slot) => (
          supportFor(slot) === NON_FIELD_SUPPORT.TIME ? (
            <TimeBindingRow
              key={slot.key}
              slot={slot}
              value={nonFieldValues[slot.key]}
              candidates={timeCandidates}
              onChange={onNonFieldChange}
            />
          ) : (
            <NonFieldBindingRow
              key={slot.key}
              slot={slot}
              value={nonFieldValues[slot.key]}
              onChange={onNonFieldChange}
            />
          )
        ))}
      </div>
    </section>
  );
}

BindingSlotsSection.propTypes = {
  /** The type's field slots, from `readSlotModel().slots`. */
  slots: PropTypes.arrayOf(PropTypes.object).isRequired,
  /** Its non-field ones, from `readSlotModel().nonFieldSlots`. */
  nonFieldSlots: PropTypes.arrayOf(PropTypes.object).isRequired,
  assignments: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.string]),
  ).isRequired,
  /** `{ time: {...}, point_mode: 'X' }` — the non-field bindings' own state. */
  nonFieldValues: PropTypes.object.isRequired,
  /**
   * The date columns of the **source** — the period names one of those, not
   * an element of the query, so a KPI has them too (see `TimeBindingRow`).
   */
  timeCandidates: PropTypes.arrayOf(PropTypes.object).isRequired,
  /** The columns the Data step exposed, in the author's order. */
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  isLoading: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onNonFieldChange: PropTypes.func.isRequired,
};

export default BindingSlotsSection;
